/**
 * Context menus when editing and working with proteins.
 */
import * as analytics from 'analytics/Analytics';
import * as asserts from 'Asserts';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as BioUtil from 'biojs/Util';
import * as ContextMenu from 'components/ContextMenu';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import FileStates from 'FileStates';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import Protein from 'molbio/aa-sequences/AaSequence';
import ProteinActions from 'molbio/aa-sequences/AaSequenceActions';
import {ProteinAnnotationView} from 'molbio/aa-sequences/AaSequenceAnnotationsView';
import {
  ProteinNamesQuery,
  ProteinNamesQueryVariables,
} from 'molbio/aa-sequences/util/__generated/ProteinNamesQuery';
import {ProteinPart} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {
  getAccessibleProteinNamesById,
  PROTEIN_NAMES_QUERY,
} from 'molbio/aa-sequences/util/AaSequenceQueryUtil';
import PolymerMapTabView from 'molbio/maps/PolymerMapTabView';
import * as sequtil from 'molbio/SeqUtil';
import PolymerMapTabState from 'molbio/tabstates/PolymerMapTabState';
import {BENCHLING_BLAST_MENU_TITLE} from 'search/blast/BLASTConstants';
import {runBLASTModalForSelection} from 'search/blast/BLASTUtil';
import {validateBLASTSequence} from 'search/blast/validation';
import {Operator} from 'search/SearchConstants';
import {deleteAnnotation} from 'toolbar/aa-sequence/AaSequenceAnnotationsPanelUtil';
import {getSelectedAnnotationId, openCreateAnnotation} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import {BasicEvent} from 'util/Types';

export interface ProteinClickableContextMenuOption extends ContextMenu.ClickableContextMenuOption {
  hide?: () => boolean | undefined;
  disabled?: () => boolean;
  menu?: Array<ProteinContextMenuOption> | null;
}

export type ProteinContextMenuOption = ProteinClickableContextMenuOption | '-';

function getOpenPartMenuOption(part: ProteinPart): ProteinContextMenuOption {
  return {
    name: 'Open part',
    click() {
      FileStates.get().openFolderItem(part.proteinId);
    },
    hide() {
      return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
    },
  };
}

function getUnlinkPartMenuOption(protein: Protein, part: ProteinPart): ProteinContextMenuOption {
  return {
    name: 'Unlink part',
    click() {
      // TODO BNCH-117227
    },
    disabled() {
      return !protein.allowResidueEdits();
    },
    hide() {
      return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
    },
  };
}

// Exported for testing only
export async function getPartMenuOptions(protein: Protein): Promise<Array<ProteinContextMenuOption>> {
  if (!BenchlingConfig.MOLBIO_ENABLE_AA_PARTS) {
    return [];
  }
  const selection = protein.selection();
  const parts = protein.getPartsInSelection(selection.start(), selection.end());
  if (parts.length === 0) {
    return [];
  } else if (
    selection.isNotEmpty() &&
    !parts.some((part) => sequtil.isRangeWithin(selection.start(), selection.end(), part.start, part.end))
  ) {
    return [];
  } else if (parts.length === 1) {
    const singlePart = parts[0];
    const partHasAnyOverlaps = protein.getPartsInSelection(singlePart.start, singlePart.end).length > 1;
    return [
      getOpenPartMenuOption(singlePart),
      {
        name: 'Delete part',
        click() {
          ProteinActions.deleteAminoAcids(protein.id, {start: singlePart.start, end: singlePart.end});
        },
        disabled() {
          return !protein.allowResidueEdits() || partHasAnyOverlaps;
        },
        hide() {
          return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
        },
      },
      {
        name: 'Replace part',
        click() {
          // TODO BNCH-121772
        },
        disabled() {
          return !protein.allowResidueEdits() || partHasAnyOverlaps;
        },
        hide() {
          return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
        },
      },
      getUnlinkPartMenuOption(protein, singlePart),
      '-',
    ];
  } else {
    const result = await doQuerySafe<ProteinNamesQuery, ProteinNamesQueryVariables>(
      {query: PROTEIN_NAMES_QUERY},
      {
        variables: {ids: protein.parts().map(({proteinId}) => proteinId)},
      }
    );
    const partNamesById = getAccessibleProteinNamesById(result);
    if (partNamesById == null) {
      asserts.assertAndContinue(false, 'Failed to fetch protein part names for context menu');
      return [];
    }
    const partsOptions = parts.map((part) => ({
      name: partNamesById[part.proteinId],
      menu: [
        {
          name: 'Select part',
          click() {
            protein.selection().setEndpoints(part.start, part.end);
          },
        },
        getOpenPartMenuOption(part),
        getUnlinkPartMenuOption(protein, part),
      ],
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    }));
    return [...partsOptions, '-'];
  }
}

export const openContextMenu = async (
  e: BasicEvent | Event,
  protein: Protein,
  proteinMapView: PolymerMapTabView<PolymerMapTabState<Protein>>
): Promise<void> => {
  const partMenuOptions = await getPartMenuOptions(protein);
  ContextMenu.view.open(e, [
    ...partMenuOptions,
    {
      name: 'Insert residues',
      click() {
        proteinMapView.showInputBubble({});
      },
      disabled() {
        return !protein.allowResidueEdits() || !protein.canInsertAt(protein.selection().start());
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
  ]);
};

export const openSelectionContextMenu = async (e: BasicEvent | Event, protein: Protein): Promise<void> => {
  // ContextMenu.view.open already stops event propagation, but we have to preemptively call stopPropagation
  // here. This function waits for a Promise to resolve, so the event may propagate before open is called.
  e.stopPropagation();
  const selectedAminoAcids = protein.residueSlice(protein.selection().start(), protein.selection().end()!);
  const hasNonStandardAminoAcids = BioUtil.containsNonStandardAminoAcids(selectedAminoAcids);
  const partMenuOptions = await getPartMenuOptions(protein);
  ContextMenu.view.open(e, [
    ...partMenuOptions,
    {
      name: 'Delete residues',
      click() {
        protein.deleteSelection();
      },
      disabled() {
        return !protein.allowResidueEdits() || !protein.isSelectionDeletable();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    {
      name: 'Create annotation',
      click() {
        analytics.track('protein', 'open_annotation_panel', {
          product: ANALYTICS_MODULE.MOLBIO,
          openedFrom: 'context_menu',
          protein_id: protein.get('id'),
        });
        openCreateAnnotation(protein.id, {
          start: protein.selection().start(),
          end: protein.selection().end(),
        });
        protein.fileState().get('toolbar').trigger('openProteinAnnotationsPanel');
      },
      disabled() {
        return !protein.allowFeatureEdits();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createAnnotation'},
    },
    {
      name: 'Copy',
      copy() {
        return selectedAminoAcids;
      },
    },
    {
      name: hasNonStandardAminoAcids
        ? 'Back translate (cannot back-translate non-standard AAs)'
        : 'Back translate',
      click() {
        protein.handleBackTranslate();
      },
      disabled() {
        return hasNonStandardAminoAcids || protein.isTransientDisplay();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE});
      },
    },
    {
      name: BENCHLING_BLAST_MENU_TITLE,
      async click() {
        await runBLASTModalForSelection(protein.selection());
      },
      disabled() {
        return (
          protein.isTransientDisplay() ||
          protein.selection().isEmpty() ||
          !validateBLASTSequence(protein.selection().getSelectionResidues(), Operator.BLASTP)
        );
      },
      hide() {
        return protein.selection().isEmpty() || !BenchlingConfig.ENABLE_BLAST_SEARCH;
      },
    },
  ]);
};

export const openAnnotationContextMenu = (
  e: BasicEvent | Event,
  protein: Protein,
  annotation: ProteinAnnotationView
): void => {
  if (protein.isTransientDisplay()) {
    return;
  }
  asserts.assert(
    getSelectedAnnotationId(protein.id) === annotation.id,
    'Expected protein UI state to be up to date before the context menu code runs.'
  );
  ContextMenu.view.open(e, [
    {
      name: 'Delete annotation',
      click() {
        deleteAnnotation(protein.id, protein.getActiveVersionId(), annotation.id, annotation.id);
      },
      disabled() {
        return !protein.allowFeatureEdits() || annotation.isFromPart();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    {
      name: 'Add to feature library',
      click() {
        ProteinActions.promptAddAnnotationToFeatureLibrary(protein.id, annotation.id);
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN});
      },
    },
  ]);
};
